//#region IMPORTS
import {
  instoreOrderTypesSelectors,
  clearInstoreOrderTypeParams,
  deleteInstoreOrderTypeAsync,
} from "../../features/instoreOrderTypes/instoreOrderTypesSlice";
import {
  DELETE_INSTORE_TYPE_SURE,
  CREATE_OR_UPDATE_INSTORE_TYPES_MODAL,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { InstoreOrderType } from "../../models/instoreOrderType";
import { useTranslation } from "react-i18next";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreOrderTypesFilterBar from "./InstoreOrderTypesFilterBar";
import InstoreOrderTypesTable from "./InstoreOrderTypesTable";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../_layout/MainContainer";
import useHQmanager from "../../hooks/useHQmanager";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import AreYouSure from "../modalTypes/AreYouSure";
import PageHeader from "../genericPageHeader/PageHeader";
import MainModal from "../_layout/MainModal";
import InstoreOrderTypeForm from "./InstoreOrderTypeForm";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function InstoreOrderTypesContainer({
  title = "rituals.instoreOrderTypes",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const instoreOrderTypes = useAppSelector(
    instoreOrderTypesSelectors.selectAll
  );
  const { loading, instoreOrderTypes_Loaded, metaData, status } =
    useAppSelector(({ instoreOrderTypes }) => instoreOrderTypes);
  const { user } = useAppSelector(({ account }) => account);

  // Local state
  const [savingDataUpload] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<InstoreOrderType>();

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);
  //#endregion

  //#region LOGIC
  // Create or update type
  const createOrUpdateTypeHandler = (instoreOrderType?: InstoreOrderType) => {
    setSelectedType(instoreOrderType);
    dispatch(openModal(CREATE_OR_UPDATE_INSTORE_TYPES_MODAL));
  };

  // Delete type warning
  const deleteTypeWarningHandler = (instoreOrderType: InstoreOrderType) => {
    setSelectedType(instoreOrderType);
    dispatch(openModal(DELETE_INSTORE_TYPE_SURE));
  };

  // Delete type confirm
  const deleteTypeHandler = async () => {
    if (selectedType) {
      await dispatch(deleteInstoreOrderTypeAsync({ id: selectedType.id }));
      dispatch(openModal(false));
    }
  };

  // Cancel create or update promo
  const cancelCRUDType = () => {
    if (!savingDataUpload) {
      setSelectedType(undefined);
      dispatch(openModal(false));
    } else {
      toast.error("Some background tasks are stil running. Please wait...");
    }
  };

  // Reset handler
  const resetHandler = () => {
    setSelectedType(undefined);
    dispatch(clearInstoreOrderTypeParams());
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={DELETE_INSTORE_TYPE_SURE} closeHandler={cancelCRUDType}>
        <AreYouSure
          confirmHandler={deleteTypeHandler}
          closeHandler={cancelCRUDType}
          message={`Are you sure you want to delete type ${selectedType?.name}?`}
          title="Delete type?"
        />
      </MainModal>

      <MainModal
        type={CREATE_OR_UPDATE_INSTORE_TYPES_MODAL}
        closeHandler={cancelCRUDType}
      >
        <InstoreOrderTypeForm
          instoreOrderType={selectedType}
          cancelCUD={cancelCRUDType}
          disabled={loading && status.includes("PendingCreateInstoreOrderType")}
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          createClickHandler={() => createOrUpdateTypeHandler(undefined)}
          createButtonValue={t("rituals.addType")}
          disabled={loading}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          hqCanAdd
          title={t(title)}
          reset={resetHandler}
        >
          <GoBackButton />
        </PageHeader>

        <InstoreOrderTypesFilterBar />

        <InstoreOrderTypesTable
          editHandler={(item: InstoreOrderType) =>
            createOrUpdateTypeHandler(item)
          }
          deleteHandler={(item: InstoreOrderType) =>
            deleteTypeWarningHandler(item)
          }
          itemsLoaded={instoreOrderTypes_Loaded}
          metaData={metaData}
          loading={loading}
          items={instoreOrderTypes}
          hide={instoreOrderTypes_Loaded && instoreOrderTypes.length <= 0}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
